import React, { useEffect, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { FunnelProvider, useFunnel } from './shared/context/FunnelContext'
import { trackAttentiveProductView } from './shared/utils/attentiveEcommerce'
import PaymentReturn from './shared/components/PaymentReturn'
import NotFound from './shared/components/NotFound'

const VariantCv1 = React.lazy(() => import('./funnels/variant-cv-1'))
const CvPaymentReturn = React.lazy(() => import('./funnels/variant-cv-1/components/CvPaymentReturn'))
const VariantCv2 = React.lazy(() => import('./funnels/variant-cv-2'))
const Cv2PaymentReturn = React.lazy(() => import('./funnels/variant-cv-2/components/CvPaymentReturn'))
const VariantCv3 = React.lazy(() => import('./funnels/variant-cv-3'))
const Cv3PaymentReturn = React.lazy(() => import('./funnels/variant-cv-3/components/CvPaymentReturn'))
const VariantCv4 = React.lazy(() => import('./funnels/variant-cv-4'))
const Cv4PaymentReturn = React.lazy(() => import('./funnels/variant-cv-4/components/CvPaymentReturn'))
const VariantCvNad = React.lazy(() => import('./funnels/variant-cv-nad'))
const NadPaymentReturn = React.lazy(() => import('./funnels/variant-cv-nad/components/NadPaymentReturn'))
const VariantCvSermorelin = React.lazy(() => import('./funnels/variant-cv-sermorelin'))
const SermorelinPaymentReturn = React.lazy(() => import('./funnels/variant-cv-sermorelin/components/SermorelinPaymentReturn'))

import { initializePromoData } from './shared/utils/promoManager'
import { getFunnelIdFromPath } from './shared/utils/funnelUtils'
import { sessionStore } from './shared/utils/sessionKey'
import { initializeGTM, initializeGoogleAnalytics, initializeGoogleAds } from './shared/utils/googleAnalytics'
import { initializeEverflow, isDirectCheckoutPaymentHit } from './shared/utils/everflow'
import { initializeClarity } from './shared/utils/clarity'
import { initializeMetaPixel } from './shared/utils/metaPixel'
import { syncSourceFromUrl } from './shared/utils/trafficSource'
import { initializeMicrosoftAds } from './shared/utils/microsoftAds'
import { initializeKatalys } from './shared/utils/katalys'
import { initializeTikTokPixel } from './shared/utils/tiktokPixel'
import { initializeWhopPixel } from './shared/utils/whopPixel'
import { useTikTokInitiateCheckoutTracking } from './shared/hooks/useTikTokInitiateCheckoutTracking'
import { addBreadcrumb } from './shared/utils/sentry'
import './App.scss'

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div style={{ textAlign: 'center' }}>
      <div className="loading-spinner" />
      <p style={{ marginTop: '1rem', color: '#666' }}>Loading...</p>
    </div>
  </div>
)

// Redirect component that preserves query strings
const RedirectWithQuery = ({ to }) => {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} replace />
}

// Component that watches for affid / funnel changes and reinitializes promo.
// Funnel id is derived from the path (/cv-1 → variant-cv-1, /cv-nad → variant-cv-nad)
// so navigating between funnels in the same tab triggers a refetch with the
// correct ACF group on the backend.
const PromoWatcher = () => {
  const location = useLocation()
  const prevAffidRef = React.useRef(null)
  const prevFunnelRef = React.useRef(null)

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    // Prefer URL affid; fall back to session so checkout refresh without ?affid= keeps promo
    const currentAffid = urlParams.get('affid') || sessionStore.getItem('affid') || ''
    const currentFunnel = getFunnelIdFromPath(location.pathname)

    // Only initialize promo if valid funnel found
    if (currentFunnel && (currentAffid !== prevAffidRef.current || currentFunnel !== prevFunnelRef.current)) {
      prevAffidRef.current = currentAffid
      prevFunnelRef.current = currentFunnel

      initializePromoData(currentAffid || null, currentFunnel)
    }
  }, [location.pathname, location.search])

  return null
}

// Keeps localStorage `source` in step with ?affid=. Pathname is a dependency
// because storage keys are scoped per funnel (e.g. / → /cv-1 keeps the query).
const TrafficSourceWatcher = () => {
  const location = useLocation()

  useEffect(() => {
    syncSourceFromUrl(location.search)
  }, [location.pathname, location.search])

  return null
}

// CV-1..CV-4: a direct hit on /checkout/payment/:caseId opens the selection
// step instead (NAD / Sermorelin checkout pages already redirect themselves).
const CV_CHECKOUT_PAYMENT_PATH = /^\/(cv-[1-4])\/checkout\/payment\/([^/?#]+)/

const DirectPaymentHitRedirect = () => {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const match = location.pathname.match(CV_CHECKOUT_PAYMENT_PATH)
    if (!match || !isDirectCheckoutPaymentHit()) return
    // Payment error returns are shown on the payment step by the checkout page.
    if (new URLSearchParams(location.search).has('error')) return

    navigate(`/${match[1]}/checkout/selection/${match[2]}${location.search}`, { replace: true })
  }, [])

  return null
}

const TikTokInitiateCheckoutWatcher = () => {
  useTikTokInitiateCheckoutTracking()
  return null
}

// Attentive "Viewed a product" when the quiz reaches plan selection.
// Retries once email is on the session, including a direct selection URL.
const AttentiveProductViewWatcher = () => {
  const { funnelData } = useFunnel()
  const location = useLocation()

  useEffect(() => {
    if (!location.pathname.includes('/checkout/selection')) return
    trackAttentiveProductView(funnelData)
  }, [location.pathname, funnelData.email, funnelData.entry_id, funnelData.phone])

  return null
}

function App() {
  // Initialize tracking and promo data on app load
  useEffect(() => {
    // Add Sentry breadcrumb for app initialization
    addBreadcrumb({
      category: 'app',
      message: 'Application initialized',
      level: 'info',
    })

    // Initialize Google Tag Manager
    initializeGTM();
    
    // Initialize Google Analytics
    initializeGoogleAnalytics();
    
    // Initialize Google Ads conversion tracking (production only)
    initializeGoogleAds();
    
    // Initialize Everflow tracking
    initializeEverflow();
    
    // Initialize Microsoft Clarity (production only)
    initializeClarity();
    
    // Initialize Meta (Facebook) Pixel (production only).
    // source must reflect ?affid= first — the pixel only loads when source=meta.
    syncSourceFromUrl();
    initializeMetaPixel();
    // Initialize Microsoft Ads UET (production only)
    initializeMicrosoftAds();

    // Initialize Katalys RevOffers affiliate tracking (production only)
    initializeKatalys();

    // Initialize TikTok Pixel (production only)
    initializeTikTokPixel();

    // Initialize Whop Pixel (production only)
    initializeWhopPixel();
    
    // Initialize promo data for the funnel inferred from the initial pathname.
    initializePromoData(null, getFunnelIdFromPath(window.location.pathname));
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <FunnelProvider>
      <BrowserRouter>
        <DirectPaymentHitRedirect />
        <PromoWatcher />
        <TrafficSourceWatcher />
        <TikTokInitiateCheckoutWatcher />
        <AttentiveProductViewWatcher />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<RedirectWithQuery to="/cv-1" />} />
            <Route path="/cv-1" element={<VariantCv1 />} />
            <Route path="/cv-1/checkout/selection/:caseId?" element={<VariantCv1 />} />
            <Route path="/cv-1/checkout/payment/:caseId?" element={<VariantCv1 />} />
            <Route path="/cv-2" element={<VariantCv2 />} />
            <Route path="/cv-2/checkout/selection/:caseId?" element={<VariantCv2 />} />
            <Route path="/cv-2/checkout/payment/:caseId?" element={<VariantCv2 />} />
            <Route path="/cv-3" element={<VariantCv3 />} />
            <Route path="/cv-3/checkout/selection/:caseId?" element={<VariantCv3 />} />
            <Route path="/cv-3/checkout/payment/:caseId?" element={<VariantCv3 />} />
            <Route path="/cv-4" element={<VariantCv4 />} />
            <Route path="/cv-4/checkout/selection/:caseId?" element={<VariantCv4 />} />
            <Route path="/cv-4/checkout/payment/:caseId?" element={<VariantCv4 />} />
            <Route path="/nad" element={<VariantCvNad />} />
            <Route path="/nad/checkout/selection/:caseId?" element={<VariantCvNad />} />
            <Route path="/nad/checkout/payment/:caseId?" element={<VariantCvNad />} />
            <Route path="/payment/return" element={<PaymentReturn />} />
            <Route path="/payment/return/cv" element={<CvPaymentReturn />} />
            <Route path="/payment/return/cv2" element={<Cv2PaymentReturn />} />
            <Route path="/payment/return/cv3" element={<Cv3PaymentReturn />} />
            <Route path="/payment/return/cv4" element={<Cv4PaymentReturn />} />
            <Route path="/payment/return/nad" element={<NadPaymentReturn />} />
            <Route path="/sermorelin" element={<VariantCvSermorelin />} />
            <Route path="/sermorelin/checkout/selection/:caseId?" element={<VariantCvSermorelin />} />
            <Route path="/sermorelin/checkout/payment/:caseId?" element={<VariantCvSermorelin />} />
            <Route path="/payment/return/sermorelin" element={<SermorelinPaymentReturn />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </FunnelProvider>
  )
}

// Wrap App with Sentry Profiler for performance monitoring
export default Sentry.withProfiler(App)
